import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import MyLayout from '../components/MyLayout';
import { fetchMyOrders, type MyOrder } from '@/lib/myAccount';

const statusColors: Record<string, string> = {
  pending: 'bg-[#F0EFED] text-[#8A8A8A]',
  paid: 'bg-[#7B1113]/10 text-[#7B1113]',
  shipped: 'bg-[#1E3A8A]/10 text-[#1E3A8A]',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

export default function MyOrdersPage() {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState<MyOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    fetchMyOrders(currentUser.id).then((rows) => {
      setOrders(rows);
      setLoading(false);
    });
  }, [currentUser]);

  return (
    <MyLayout title={t('my.orders.title', 'Order History')}>
      <div className="bg-white rounded-2xl border border-[#E8E8E6] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#8A8A8A]">{t('common.loading')}</div>
        ) : orders.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#8A8A8A]">
            {t('my.orders.empty', 'No orders yet')}
          </div>
        ) : (
          <div className="flex flex-col">
            {orders.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between px-6 py-5 border-b border-[#E8E8E6] last:border-0"
              >
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-[#2A2A2A]">{order.orderNo}</span>
                  <span className="text-xs text-[#8A8A8A]">{order.placedAt.slice(0, 10)}</span>
                  <span className="text-xs text-[#8A8A8A]">{order.items.map((i) => i.name).join(', ')}</span>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${statusColors[order.status]}`}>
                    {order.status}
                  </span>
                  <span className="text-sm font-medium text-[#2A2A2A]">৳{order.totalBdt.toLocaleString()}</span>
                  <Link to={prefix + `/my/orders/${order.id}`} className="text-xs text-[#7B1113] hover:underline">
                    {t('my.orderDetail', 'Order Detail')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MyLayout>
  );
}
